import pdfMake from 'pdfmake/build/pdfmake'
import pdfFonts from 'pdfmake/build/vfs_fonts'
import Vue from 'vue'
export const InsightsEventBus = new Vue()

pdfMake.vfs = pdfFonts.pdfMake.vfs

const pageMargins = [20, 20, 20, 20]

const chartMargins = {
  page: 20,
  fullChart: {
    top: 20,
    bottom: 20,
    side: 20
  },
  gridCell: [10, 10, 10, 10],
  gridSpacing: 10,
  rowSpacing: 20
}

export class SimplePDFGenerator {
  constructor () {
    this.pageWidth = 842
    this.pageHeight = 600

    this.availablePageWidth = this.pageWidth - pageMargins[0] - pageMargins[2]
    this.availablePageHeight = this.pageHeight - pageMargins[1] - pageMargins[3]

    this.chartWidth = (this.availablePageWidth - chartMargins.page) / 2
    this.chartHeight = (this.availablePageHeight - chartMargins.page) / 2
    this.maxRowHeight = 300
  }

  async extractCharts (containerElement) {
    if (!containerElement) throw new Error('Container element not found')
    const charts = []

    const smallSvgs = containerElement.querySelectorAll('.small-chart svg')
    let maxWidth = 0

    const filteredSmallSvgs = Array.from(smallSvgs).filter(svg => {
      return !svg.closest('[data-no-data]')
    })

    filteredSmallSvgs.forEach(svg => {
      const rect = svg.getBoundingClientRect()
      if (rect.width > 100 && rect.height > 100) {
        if (rect.width > maxWidth) maxWidth = rect.width
      }
    })

    const targetWidth = (this.availablePageWidth - chartMargins.page) / 2
    const scale = maxWidth > targetWidth ? targetWidth / maxWidth : 1

    filteredSmallSvgs.forEach(svg => {
      const rect = svg.getBoundingClientRect()
      if (rect.width > 100 && rect.height > 100) {
        charts.push({
          svg: this.processSVGForPDF(
            svg,
            rect.width * scale,
            rect.height * scale,
            false
          ),
          width: rect.width * scale,
          height: rect.height * scale,
          type: 'small'
        })
      }
    })

    const fullSvgs = containerElement.querySelectorAll('.pdf-mode-chart svg')

    const filteredFullSvgs = Array.from(fullSvgs).filter(svg => {
      return !svg.closest('[data-no-data]')
    })

    const availableWidth = this.availablePageWidth - (chartMargins.fullChart.side * 2)
    const availableHeight = this.availablePageHeight - chartMargins.fullChart.top - chartMargins.fullChart.bottom

    filteredFullSvgs.forEach(svg => {
      const rect = svg.getBoundingClientRect()
      if (rect.width > 100 && rect.height > 100) {
        const widthScale = availableWidth / rect.width
        const heightScale = availableHeight / rect.height
        const scale = Math.min(widthScale, heightScale, 1)

        charts.push({
          svg: this.processSVGForPDF(
            svg,
            rect.width * scale,
            rect.height * scale,
            true
          ),
          width: rect.width * scale,
          height: rect.height * scale,
          type: 'full',
          topMargin: chartMargins.fullChart.top,
          bottomMargin: chartMargins.fullChart.bottom
        })
      }
    })

    return Promise.all(charts.map(async c => ({
      ...c,
      svg: await c.svg
    })))
  }

  fixSVGColors (svgClone) {
    const textElements = svgClone.querySelectorAll('text, tspan')
    textElements.forEach(textElement => {
      const fillColor = textElement.getAttribute('fill') || 'none'
      textElement.setAttribute('stroke', fillColor)
      textElement.setAttribute('stroke-width', '0')
    })
  }

  async processSVGForPDF (originalSvg, targetWidth, targetHeight, fullView = false) {
    const svgClone = originalSvg.cloneNode(true)
    this.fixSVGColors(svgClone)

    const currentWidth = parseInt(svgClone.getAttribute('width')) || svgClone.getBoundingClientRect().width
    const currentHeight = parseInt(svgClone.getAttribute('height')) || svgClone.getBoundingClientRect().height

    if (!svgClone.getAttribute('viewBox')) {
      svgClone.setAttribute('viewBox', `0 0 ${currentWidth} ${currentHeight}`)
    }

    svgClone.removeAttribute('width')
    svgClone.removeAttribute('height')
    svgClone.setAttribute('width', targetWidth)
    svgClone.setAttribute('height', targetHeight)
    svgClone.setAttribute('preserveAspectRatio', 'xMidYMid meet')

    return new XMLSerializer().serializeToString(svgClone)
  }

  groupChartsFor2x2Layout (charts) {
    const groups = []
    for (let i = 0; i < charts.length; i += 4) {
      groups.push(charts.slice(i, i + 4))
    }
    return groups
  }

  makeRowTable (rowItems) {
    const items = rowItems.filter(Boolean)
    if (items.length === 0) return null

    const rowHeight = Math.max(...items.map(c => c.height || 0), 0)

    const cells = items.map(c => ({
      svg: c.svg,
      width: c.width,
      height: c.height,
      alignment: 'center',
      margin: chartMargins.gridCell
    }))

    const widths = Array(cells.length).fill('*')

    const node = {
      table: {
        headerRows: 0,
        widths,
        heights: [rowHeight],
        body: [cells]
      },
      layout: 'noBorders'
    }

    const actualRowHeight = rowHeight + chartMargins.gridCell[1] + chartMargins.gridCell[3]

    return { node, rowHeight: actualRowHeight }
  }

  create2x2ChartBlock (chartGroup) {
    const firstRow = this.makeRowTable(chartGroup.slice(0, 2))
    const secondRow = this.makeRowTable(chartGroup.slice(2, 4))

    if (!firstRow && !secondRow) return { text: '' }

    if (firstRow && !secondRow) {
      const topMargin = Math.max(0, (this.availablePageHeight - firstRow.rowHeight) / 2)
      return {
        ...firstRow.node,
        margin: [0, topMargin, 0, 0]
      }
    }
    if (!firstRow && secondRow) {
      const topMargin = Math.max(0, (this.availablePageHeight - secondRow.rowHeight) / 2)
      return {
        ...secondRow.node,
        margin: [0, topMargin, 0, 0]
      }
    }

    const totalContentHeight = firstRow.rowHeight + secondRow.rowHeight + chartMargins.rowSpacing
    const topMargin = Math.max(0, (this.availablePageHeight - totalContentHeight) / 2)

    return {
      margin: [0, topMargin, 0, 0],
      stack: [
        firstRow.node,
        { text: '', margin: [0, chartMargins.rowSpacing, 0, 0] },
        secondRow.node
      ]
    }
  }

  createDocumentDefinition (charts) {
    const smallCharts = charts.filter(c => c.type === 'small')
    const fullCharts = charts.filter(c => c.type === 'full')

    const content = []

    const chartGroups = this.groupChartsFor2x2Layout(smallCharts)
    chartGroups.forEach((group, groupIndex) => {
      if (groupIndex > 0) {
        content.push({ text: '', pageBreak: 'before' })
      }
      content.push(this.create2x2ChartBlock(group))
    })

    fullCharts.forEach(chart => {
      content.push({ text: '', pageBreak: 'before' })
      content.push({
        svg: chart.svg,
        width: chart.width,
        height: chart.height,
        alignment: 'center',
        margin: [0, chart.topMargin, 0, chart.bottomMargin]
      })
    })

    return {
      pageSize: 'A4',
      pageOrientation: 'landscape',
      pageMargins,
      content,
      styles: {
        header: {
          fontSize: 16,
          bold: true
        }
      }
    }
  }

  async generatePDFPreview (containerElement) {
    const charts = await this.extractCharts(containerElement)
    if (charts.length === 0) throw new Error('No charts found to export')

    const docDefinition = this.createDocumentDefinition(charts)
    pdfMake.createPdf(docDefinition).open()

    return {
      success: true,
      chartCount: charts.length,
      pagesCount: Math.ceil(charts.length / 4)
    }
  }
}

const pdfGenerator = new SimplePDFGenerator()
export const generateInsightsPDF = containerElement => pdfGenerator.generatePDFPreview(containerElement)

export const emitChartFinished = (component, noData) => {
  component.$nextTick(() => {
    if (noData) {
      component.$el?.setAttribute('data-no-data', 'true')
    }
    InsightsEventBus.$emit('chart-finished', {
      componentName: component.$options.name,
      pdfMode: component.$props.fullView,
      noData
    })
  })
}
